import React, { useState, useEffect } from "react";
import { Table, Button, Dropdown, Menu, Popover } from "antd";
import { Download, Filter, Calendar, TrendingUp } from "react-feather";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import exportService from "../../utils/exportService";
import Swal from "sweetalert2";
import PrintReportPreview from "../../reports/PrintReportPreview";
import moment from "moment";
import { useTranslation } from "react-i18next";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Mock data for testing
const mockData = [
  { report_date: "2025-07-19T00:00:00Z", room_revenue: 40000, service_revenue: 4500, other_revenue: 800, total_revenue: 45300, occupancy_rate: 68.75, adr: 115.00, revpar: 79.06, net_revenue: 37850 },
  { report_date: "2025-07-20T00:00:00Z", room_revenue: 42000, service_revenue: 4800, other_revenue: 900, total_revenue: 47700, occupancy_rate: 71.43, adr: 120.00, revpar: 85.71, net_revenue: 39850 },
  { report_date: "2025-07-21T00:00:00Z", room_revenue: 38000, service_revenue: 4200, other_revenue: 700, total_revenue: 42900, occupancy_rate: 65.22, adr: 110.00, revpar: 71.74, net_revenue: 35850 },
];

const DailyRevenueReport = () => {
  const { t } = useTranslation();
const apiURL = process.env.REACT_APP_API_URL || "http://localhost:4000";

  const [loading, setLoading] = useState(false);
  const [revenueData, setRevenueData] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(2, "days").startOf("day"),
    endDate: moment(),
  });
  const [previewVisible, setPreviewVisible] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [exportFooter, setExportFooter] = useState([]);
  const [totalsForPageFooter, setTotalsForPageFooter] = useState([]);
  const [totalColumns, setTotalColumns] = useState([]);
  const [reportTitle] = useState(t("daily_revenue_report"));

  useEffect(() => {
    const filteredInitialData = mockData.filter((item) =>
      moment(item.report_date).isBetween(dateRange.startDate, dateRange.endDate, null, "[]")
    );
    setRevenueData(filteredInitialData);
    setLoading(false);
  }, [dateRange]);

  const columns = [
    { title: t("report_date"), dataIndex: "report_date", key: "report_date", render: (text) => moment(text).format("DD MMM YYYY") },
    { title: t("room_revenue"), dataIndex: "room_revenue", key: "room_revenue", render: (text) => `₹${Number(text || 0).toFixed(2)}` },
    { title: t("service_revenue"), dataIndex: "service_revenue", key: "service_revenue", render: (text) => `₹${Number(text || 0).toFixed(2)}` },
    { title: t("other_revenue"), dataIndex: "other_revenue", key: "other_revenue", render: (text) => `₹${Number(text || 0).toFixed(2)}` },
    { title: t("total_revenue"), dataIndex: "total_revenue", key: "total_revenue", render: (text) => `₹${Number(text || 0).toFixed(2)}` },
    { title: t("occupancy_rate"), dataIndex: "occupancy_rate", key: "occupancy_rate", render: (text) => `${text || 0}%` },
    { title: t("adr"), dataIndex: "adr", key: "adr", render: (text) => `₹${Number(text || 0).toFixed(2)}` },
    { title: t("revpar"), dataIndex: "revpar", key: "revpar", render: (text) => `₹${Number(text || 0).toFixed(2)}` },
    { title: t("net_revenue"), dataIndex: "net_revenue", key: "net_revenue", render: (text) => `₹${Number(text || 0).toFixed(2)}` },
  ];

  const totals = revenueData.reduce(
    (acc, item) => ({
      room_revenue: acc.room_revenue + (item.room_revenue || 0),
      service_revenue: acc.service_revenue + (item.service_revenue || 0),
      other_revenue: acc.other_revenue + (item.other_revenue || 0),
      total_revenue: acc.total_revenue + (item.total_revenue || 0),
      net_revenue: acc.net_revenue + (item.net_revenue || 0),
    }),
    { room_revenue: 0, service_revenue: 0, other_revenue: 0, total_revenue: 0, net_revenue: 0 }
  );

  const barChartData = {
    labels: revenueData.map((item) => moment(item.report_date).format("DD MMM")),
    datasets: [
      { label: t("room_revenue"), data: revenueData.map((item) => item.room_revenue || 0), backgroundColor: "#28a745" },
      { label: t("service_revenue"), data: revenueData.map((item) => item.service_revenue || 0), backgroundColor: "#007bff" },
      { label: t("other_revenue"), data: revenueData.map((item) => item.other_revenue || 0), backgroundColor: "#ffc107" },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: { y: { beginAtZero: true, title: { display: true, text: `₹ (${t("revenue")})` } } },
    plugins: { legend: { position: "top" }, title: { display: true, text: t("daily_revenue_trends") } },
  };

  const handleDateRangeChange = (param) => {
    setDateRange((prevRange) => {
      let newRange;
      if (typeof param === "string") {
        if (param === "prev") newRange = { startDate: prevRange.startDate.subtract(1, "day"), endDate: prevRange.endDate.subtract(1, "day") };
        else if (param === "next") newRange = { startDate: prevRange.startDate.add(1, "day"), endDate: prevRange.endDate.add(1, "day") };
        else return prevRange;
      } else {
        const { start, end } = param;
        newRange = { startDate: moment(start).startOf("day"), endDate: moment(end).startOf("day") };
      }
      if (!newRange.startDate.isValid() || !newRange.endDate.isValid()) {
        Swal.fire({ title: t("error"), text: t("invalid_date_selected"), icon: "error" });
        return prevRange;
      }
      if (newRange.startDate.isAfter(newRange.endDate)) {
        Swal.fire({ title: t("error"), text: t("start_date_after_end_date"), icon: "error" });
        return prevRange;
      }
      const filteredMockData = mockData.filter((item) =>
        moment(item.report_date).isBetween(newRange.startDate, newRange.endDate, null, "[]")
      );
      setRevenueData(filteredMockData);
      return newRange;
    });
  };

  const handleExport = (format) => {
    if (!revenueData.length && format !== "print") {
      Swal.fire({ title: t("error"), text: t("no_data_available_for_export"), icon: "error" });
      return;
    }
    const exportData = revenueData.map((item) => ({
      report_date: moment(item.report_date).format("DD MMM YYYY"),
      room_revenue: `₹${Number(item.room_revenue || 0).toFixed(2)}`,
      service_revenue: `₹${Number(item.service_revenue || 0).toFixed(2)}`,
      other_revenue: `₹${Number(item.other_revenue || 0).toFixed(2)}`,
      total_revenue: `₹${Number(item.total_revenue || 0).toFixed(2)}`,
      occupancy_rate: `${item.occupancy_rate || 0}%`,
      adr: `₹${Number(item.adr || 0).toFixed(2)}`,
      revpar: `₹${Number(item.revpar || 0).toFixed(2)}`,
      net_revenue: `₹${Number(item.net_revenue || 0).toFixed(2)}`,
    }));
    const exportHeaders = columns.map((col) => ({ key: col.key, label: col.title }));
    const exportFooter = [
      { key: "total", value: t("total") },
      { key: "room_revenue", value: `₹${totals.room_revenue.toFixed(2)}`, currency: true },
      { key: "service_revenue", value: `₹${totals.service_revenue.toFixed(2)}`, currency: true },
      { key: "other_revenue", value: `₹${totals.other_revenue.toFixed(2)}`, currency: true },
      { key: "total_revenue", value: `₹${totals.total_revenue.toFixed(2)}`, currency: true },
      { key: "net_revenue", value: `₹${totals.net_revenue.toFixed(2)}`, currency: true },
    ];
    const filename = `daily_revenue_report_${dateRange.startDate.format("YYYY-MM-DD")}_${dateRange.endDate.format("YYYY-MM-DD")}`;
    if (format === "xls") exportService.exportToXLS(exportData, exportHeaders, `${filename}.xlsx`, reportTitle);
    else if (format === "csv") exportService.exportToCSV(exportData, exportHeaders, `${filename}.csv`);
    else if (format === "pdf") exportService.exportToPDF(exportData, exportHeaders, reportTitle, `${filename}.pdf`, false, true, exportFooter, exportFooter);
    else if (format === "preview") {
      setExportData(exportData);
      setExportHeaders(exportHeaders);
      setExportFooter(exportFooter);
      setTotalsForPageFooter(exportFooter);
      setTotalColumns(["room_revenue", "service_revenue", "other_revenue", "total_revenue", "net_revenue"]);
      setPreviewVisible(true);
    } else if (format === "print") window.print();
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="xls" onClick={() => handleExport("xls")}>{t("export_xls")}</Menu.Item>
      <Menu.Item key="csv" onClick={() => handleExport("csv")}>{t("export_csv")}</Menu.Item>
      <Menu.Item key="pdf" onClick={() => handleExport("pdf")}>{t("export_pdf")}</Menu.Item>
      <Menu.Item key="preview" onClick={() => handleExport("preview")}>{t("preview")}</Menu.Item>
      <Menu.Item key="print" onClick={() => handleExport("print")}>{t("print")}</Menu.Item>
    </Menu>
  );

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons mb-4" style={{ background: "#f8f9fa", padding: "15px 20px", borderRadius: "8px" }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <h2 className="mb-0 d-flex align-items-center">
                <TrendingUp size={22} className="text-primary me-2" />
                {t("daily_revenue_report")}
              </h2>
              <DateRangePicker
                initialSettings={{
                  singleDatePicker: false,
                  showDropdowns: true,
                  startDate: dateRange.startDate.toDate(),
                  endDate: dateRange.endDate.toDate(),
                  ranges: {
                    Today: [moment().startOf("day").toDate(), moment().startOf("day").toDate()],
                    Yesterday: [moment().subtract(1, "days").startOf("day").toDate(), moment().subtract(1, "days").startOf("day").toDate()],
                    "Last 7 Days": [moment().subtract(7, "days").startOf("day").toDate(), moment().startOf("day").toDate()],
                    "Last 30 Days": [moment().subtract(30, "days").startOf("day").toDate(), moment().startOf("day").toDate()],
                    "This Month": [moment().startOf("month").toDate(), moment().endOf("month").toDate()],
                    "Last Month": [moment().subtract(1, "month").startOf("month").toDate(), moment().subtract(1, "month").endOf("month").toDate()],
                  },
                  locale: { format: "DD MMM YYYY", applyLabel: "Select", cancelLabel: "Cancel" },
                }}
                onCallback={handleDateRangeChange}
              >
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
                    style={{ width: "220px", height: "38px", fontSize: "16px", fontWeight: "bold", textAlign: "center", padding: "0 10px", borderRight: "none" }}
                    readOnly
                  />
                  <div className="input-group-append">
                    <span className="input-group-text" style={{ height: "38px", backgroundColor: "#fff", borderLeft: "none", borderRadius: "0 4px 4px 0", cursor: "pointer" }}>
                      <Calendar size={16} style={{ color: "#495057" }} />
                    </span>
                  </div>
                </div>
              </DateRangePicker>
            </div>
            <div className="d-flex align-items-center gap-3">
              <Popover content={<div style={{ width: "250px", padding: "10px" }}><p>No filters available</p></div>} title={<span className="font-medium">{t("filters")}</span>} trigger="click" placement="bottomRight">
                <Button className="btn btn-link p-0" style={{ fontSize: "14px", color: "#007bff" }}>
                  <Filter size={16} style={{ marginRight: "5px" }} /> {t("filter")}
                </Button>
              </Popover>
              <Dropdown overlay={downloadMenu} trigger={["click"]}>
                <Button type="primary" style={{ height: "38px", display: "flex", alignItems: "center", padding: "0 12px", fontSize: "14px" }}>
                  <Download size={16} style={{ marginRight: "5px" }} /> {t("download")}
                </Button>
              </Dropdown>
            </div>
          </div>
          <p className="mt-2 mb-0" style={{ color: "#666", fontFamily: "'Roboto', sans-serif" }}>
            {t("breakdown_of_daily_earnings_from_rooms_and_services")}
          </p>
        </div>
        {loading ? (
          <div className="text-center my-5">
            <div className="loader">
              <div className="dot"></div><div className="dot"></div><div className="dot"></div><div className="dot"></div><div className="dot"></div>
            </div>
          </div>
        ) : revenueData.length === 0 ? (
          <div className="no-data-container text-center mt-4" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
            <img src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg" alt="No data available" style={{ width: "150px", marginBottom: "20px" }} />
            <h3>{t("no_data")}</h3>
            <p>{t("no_daily_revenue_data")}</p>
          </div>
        ) : (
          <>
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
                  <div className="card-header" style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}>
                    <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      {t("daily_revenue_trends")} (Total Revenue: ₹{totals.total_revenue.toFixed(2)})
                    </h5>
                    <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#28a745" }} />
                  </div>
                  <div className="card-body" style={{ padding: "10px", height: "300px" }}>
                    <div style={{ height: "100%", width: "100%" }}>
                      <Bar data={barChartData} options={barChartOptions} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row g-0">
              <div className="col-md-12">
                <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
                  <div className="card-header" style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}>
                    <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      {t("daily_revenue_details")} (Total Revenue: ₹{totals.total_revenue.toFixed(2)})
                    </h5>
                    <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#007bff" }} />
                  </div>
                  <div className="card-body p-0">
                    <Table
                      columns={columns}
                      dataSource={revenueData}
                      rowKey="report_date"
                      pagination={false}
                      bordered
                      className="daybook-table"
                      style={{ margin: 0 }}
                      summary={() => (
                        <Table.Summary.Row style={{ backgroundColor: "#e6f4ea", fontWeight: "700", fontSize: "15px" }}>
                          <Table.Summary.Cell>{t("total")}</Table.Summary.Cell>
                          <Table.Summary.Cell></Table.Summary.Cell>
                          <Table.Summary.Cell>{`₹${totals.room_revenue.toFixed(2)}`}</Table.Summary.Cell>
                          <Table.Summary.Cell>{`₹${totals.service_revenue.toFixed(2)}`}</Table.Summary.Cell>
                          <Table.Summary.Cell>{`₹${totals.other_revenue.toFixed(2)}`}</Table.Summary.Cell>
                          <Table.Summary.Cell>{`₹${totals.total_revenue.toFixed(2)}`}</Table.Summary.Cell>
                          <Table.Summary.Cell></Table.Summary.Cell>
                          <Table.Summary.Cell></Table.Summary.Cell>
                          <Table.Summary.Cell>{`₹${totals.net_revenue.toFixed(2)}`}</Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <PrintReportPreview
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          reportTitle={reportTitle}
          propertyName=""
          data={exportData}
          headers={exportHeaders}
          footerData={totalsForPageFooter}
          totalColumns={totalColumns}
          apiEndpoint="transactions/generate-pdf"
          apiUrl={apiURL}
        />
        <style jsx>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
          .loader { display: flex; justify-content: center; align-items: center; }
          .dot { width: 10px; height: 10px; background-color: #007bff; border-radius: 50%; margin: 0 5px; animation: dot-bounce 1.4s infinite ease-in-out both; }
          .dot:nth-child(1) { animation-delay: -0.32s; }
          .dot:nth-child(2) { animation-delay: -0.16s; }
          @keyframes dot-bounce { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); } }
          .daybook-table .ant-table-thead > tr > th { background: #ffffff; font-weight: 600; padding: 8px 16px; font-size: 14px; }
          .daybook-table .ant-table-tbody > tr > td { padding: 12px 18px; font-size: 14px; height: 60px; }
          .daybook-table .ant-table { border: 1px solid #dee2e6; }
          .daybook-table .ant-table-tbody > tr.ant-table-row:hover > td { background: #f5f5f5; }
          .form-control { border-radius: 4px; border: 1px solid #d9d9d9; padding: 6px 12px; font-size: 14px; }
        `}</style>
      </div>
    </div>
  );
};

export default DailyRevenueReport;

